/* eslint-disable no-await-in-loop */
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useEditorContext } from '@/components/demands-v2/editor/context/editor-provider';
import { AgentChatPlugin } from '@/components/demands-v2/editor/plugins/agent-chat-plugin/agent-chat-plugin';
import {
  useGetDemandSectionsAnalysisQuery,
  useTriggerDemandSectionsAnalysisMutation,
} from '@/services/api/demandExampleService';
import { AgentChatAttachment } from '@/services/types/agent-chat-types';
import {
  DemandSectionsApiResponse,
  SectionDetailFromApi,
} from '@/services/types/demand-example-types';

import {
  StreamOptions,
} from "./useAgentChatStream";

interface UseOneShotDemandProps {
  matterId: string;
  threadId: string;
  onUploadAttachment: (file: File) => Promise<AgentChatAttachment>;
  sendMessage: (
    messageHtml: string,
    attachments: AgentChatAttachment[],
    opts?: StreamOptions,
  ) => Promise<void>;
  isStreaming: boolean;
}

interface UseOneShotDemandReturn {
  isAnalyzingSections: boolean;
  isSubmittingSections: boolean;
  analyzedSections: Record<string, any>;
  showSectionsDialog: boolean;
  currentAnalysisStatus: string;
  handleOneShotDemandSelected: (demandId: string) => Promise<void>;
  handleSubmitSections: () => Promise<void>;
  handleStopExecution: () => void;
  setShowSectionsDialog: (show: boolean) => void;
  setAnalyzedSections: (sections: Record<string, any>) => void;
}

const POLLING_INTERVAL = 5000; // 5 seconds

const SECTION_ORDER: string[] = [
  'introduction',
  'liability',
  'treatment',
  'medical_costs',
  'pain_and_suffering',
  'request_for_policy_disclosure',
  'demand',
];

const fetchWithRetry = async (
  url: string,
  maxRetries = 3,
): Promise<Response> => {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt += 1) {
    try {
      const response = await fetch(url, {
        mode: 'cors',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      lastError = error as Error;
      if (attempt < maxRetries - 1) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = 2 ** attempt * 1000;
        await new Promise((resolve) => {
          setTimeout(resolve, delay);
        });
      }
    }
  }

  throw lastError || new Error('Failed to fetch after all retries');
};

export const useOneShotDemand = ({
  matterId,
  threadId,
  onUploadAttachment,
  sendMessage,
  isStreaming,
}: UseOneShotDemandProps): UseOneShotDemandReturn => {
  // Track isStreaming in a ref for use in polling loops
  const isStreamingRef = useRef(isStreaming);
  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);
  const { editor: workspaceEditor } = useEditorContext();
  const [triggerAnalysis, { isLoading: isTriggeringAnalysis }] =
    useTriggerDemandSectionsAnalysisMutation();

  const [currentDemandExampleId, setCurrentDemandExampleId] =
    useState<string>('');
  const [effectivePollingStatus, setEffectivePollingStatus] =
    useState<string>('');

  const queryArg = currentDemandExampleId || skipToken;
  const { data: analysisPollData, isLoading: isLoadingAnalysisPoll } =
    useGetDemandSectionsAnalysisQuery(queryArg, {
      pollingInterval: POLLING_INTERVAL,
      skip: !currentDemandExampleId,
      refetchOnMountOrArgChange: true,
    });

  const [analyzedSectionsInternal, setAnalyzedSectionsInternal] = useState<
    Record<string, Partial<SectionDetailFromApi>>
  >({});
  const [_showSectionsDialog, _setShowSectionsDialogInternal] = useState(false);
  const [userHasClosedModal, setUserHasClosedModal] = useState(false);

  const [isSubmittingSections, setIsSubmittingSections] = useState(false);
  const shouldStopExecutionRef = useRef(false);
  const isProcessingSectionRef = useRef(false);
  const [currentActualAnalysisStatus, setCurrentActualAnalysisStatus] =
    useState<string>('');
  const currentDemandExampleIdRef = useRef(currentDemandExampleId);

  useEffect(() => {
    currentDemandExampleIdRef.current = currentDemandExampleId;
  }, [currentDemandExampleId]);

  const setShowSectionsDialog = (show: boolean) => {
    if (!show) {
      // Clear all state when dialog is closed
      setCurrentDemandExampleId('');
      setEffectivePollingStatus('');
      setCurrentActualAnalysisStatus('');
      setAnalyzedSectionsInternal({});
      setUserHasClosedModal(true);
      _setShowSectionsDialogInternal(false);
      console.log(
        '[useOneShotDemand] setShowSectionsDialog (custom): Hiding dialog, clearing all state.',
      );
    } else {
      _setShowSectionsDialogInternal(true);
    }
  };

  useEffect(() => {
    console.log('[useOneShotDemand] Polling useEffect (Top): ', {
      currentDemandExampleId: currentDemandExampleIdRef.current,
      analysisPollData,
      isLoadingAnalysisPoll,
      effectivePollingStatus,
      _showSectionsDialog,
      userHasClosedModal,
    });

    // If demand ID is empty or changed, clear all state
    if (!currentDemandExampleIdRef.current) {
      console.log(
        '[useOneShotDemand] Polling useEffect: currentDemandExampleId is empty. Clearing all state.',
      );
      setCurrentActualAnalysisStatus('');
      setEffectivePollingStatus('');
      setAnalyzedSectionsInternal({});
      _setShowSectionsDialogInternal(false);
      setUserHasClosedModal(true);
      return;
    }

    if (analysisPollData && currentDemandExampleIdRef.current) {
      const apiResponse = analysisPollData as DemandSectionsApiResponse;
      const newStatus = apiResponse.status;

      console.log(
        '[useOneShotDemand] Polling useEffect: API Status from response:',
        newStatus,
        'Full API Response Casted:',
        apiResponse,
      );
      setCurrentActualAnalysisStatus(newStatus || '');
      setEffectivePollingStatus(newStatus || '');

      if (newStatus === 'COMPLETED') {
        console.log(
          '[useOneShotDemand] Polling useEffect: Status COMPLETED. Full API Response:',
          apiResponse,
        );
        const sectionsArray = apiResponse.sections;
        console.log(
          '[useOneShotDemand] Polling useEffect: Extracted sectionsArray from apiResponse.sections:',
          sectionsArray,
        );

        const sectionsDataForDialog = Array.isArray(sectionsArray)
          ? sectionsArray.reduce(
              (
                acc: Record<string, any>,
                section: SectionDetailFromApi,
                index: number,
              ) => {
                console.log(
                  `[DEBUG] Iteration ${index}, Current section:`,
                  JSON.stringify(section),
                );
                const sectionName = section?.section_name;

                const isSectionNameString = typeof sectionName === 'string';

                const trimmedSectionName = isSectionNameString
                  ? sectionName.trim()
                  : null;

                const isTrimmedNameValid =
                  trimmedSectionName && trimmedSectionName !== '';

                if (section && isSectionNameString && isTrimmedNameValid) {
                  acc[trimmedSectionName!] = {
                    confidence: section.confidence,
                    bounding_boxes: section.bounding_boxes,
                    block_ids: section.block_ids,
                    image_url: section.image_url,
                  };
                } else {
                  console.warn(
                    `[DEBUG] Iteration ${index}: Skipping section. Reason - section_name: ${sectionName}, isString: ${isSectionNameString}, isValidTrimmed: ${isTrimmedNameValid}`,
                  );
                }
                return acc;
              },
              {} as Record<string, any>,
            )
          : {};

        console.log(
          '[useOneShotDemand] Polling useEffect: Processed sectionsDataForDialog:',
          sectionsDataForDialog,
        );
        setAnalyzedSectionsInternal(sectionsDataForDialog);

        if (
          Object.keys(sectionsDataForDialog).length > 0 &&
          !userHasClosedModal
        ) {
          console.log(
            '[useOneShotDemand] Polling useEffect: Status COMPLETED with data AND !userHasClosedModal. Showing sections dialog.',
          );
          _setShowSectionsDialogInternal(true);
        } else {
          console.log(
            '[useOneShotDemand] Polling useEffect: Not showing dialog - either no data or userHasClosedModal is true',
          );
          _setShowSectionsDialogInternal(false);
        }
      } else if (newStatus === 'FAILED') {
        console.log(
          '[useOneShotDemand] Polling useEffect: Status FAILED. Hiding dialog.',
        );
        toast.error(
          apiResponse.error ||
            apiResponse.message ||
            'Sections analysis failed on the backend.',
        );
        setAnalyzedSectionsInternal({});
        _setShowSectionsDialogInternal(false);
        setUserHasClosedModal(true);
      } else if (newStatus === 'PENDING' || newStatus === 'PROCESSING') {
        console.log(
          '[useOneShotDemand] Polling useEffect: Status PENDING or PROCESSING.',
        );
        setAnalyzedSectionsInternal({});
      }
    } else {
      console.log(
        '[useOneShotDemand] Polling useEffect: No analysisPollData or no currentDemandExampleId. Current ID:',
        currentDemandExampleIdRef.current,
      );
    }
  }, [
    analysisPollData,
    isLoadingAnalysisPoll,
    effectivePollingStatus,
    isSubmittingSections,
    currentActualAnalysisStatus,
    userHasClosedModal,
    isTriggeringAnalysis,
  ]);

  const isSystemTrulyAnalyzing =
    isTriggeringAnalysis ||
    (!!currentDemandExampleId &&
      (isLoadingAnalysisPoll ||
        effectivePollingStatus === 'PENDING' ||
        effectivePollingStatus === 'PROCESSING'));

  const handleOneShotDemandSelected = async (demandId: string) => {
    console.log(
      '[useOneShotDemand] handleOneShotDemandSelected: setting currentDemandExampleId to',
      demandId,
    );

    // Set loading state first
    setEffectivePollingStatus('PENDING');
    setCurrentActualAnalysisStatus('PENDING');
    setUserHasClosedModal(false);

    // Clear previous analysis data
    setAnalyzedSectionsInternal({});
    _setShowSectionsDialogInternal(false);

    // Set the new demand ID last
    setCurrentDemandExampleId(demandId);

    try {
      // Then trigger the analysis
      const triggerResult = await triggerAnalysis({
        demandExampleId: demandId,
      }).unwrap();
      console.log(
        '[useOneShotDemand] handleOneShotDemandSelected: triggerAnalysis success:',
        triggerResult,
      );
      toast.info(triggerResult.message || 'Sections analysis request sent.');
    } catch (error: any) {
      console.error(
        '[useOneShotDemand] handleOneShotDemandSelected: Error triggering one-shot demand analysis:',
        error,
      );
      const errorMessage =
        error.data?.error ||
        error.message ||
        'Failed to start document sections analysis.';
      toast.error(errorMessage);
      // Clear all state on error
      setCurrentDemandExampleId('');
      setEffectivePollingStatus('');
      setCurrentActualAnalysisStatus('');
      setUserHasClosedModal(false);
    }
  };

  const waitForAI = async (context: string, maxAttempts = 120) => {
    let attempts = 0;
    while (isStreamingRef.current && attempts < maxAttempts) {
      if (shouldStopExecutionRef.current) {
        break;
      }
      await new Promise((resolve) => {
        setTimeout(resolve, 500);
      });
      attempts += 1;
    }
    if (attempts >= maxAttempts && isStreamingRef.current) {
      toast.info(`AI is taking a while (${context}). Continuing cautiously.`);
    }
  };

  const handleSubmitSections = async () => {
    if (
      !analyzedSectionsInternal ||
      Object.keys(analyzedSectionsInternal).length === 0
    ) {
      toast.error('No analyzed sections to submit.');
      return;
    }

    setIsSubmittingSections(true);
    shouldStopExecutionRef.current = false;
    _setShowSectionsDialogInternal(false);
    setUserHasClosedModal(true);
    setCurrentDemandExampleId('');
    setEffectivePollingStatus('');

    const baseInstruction = `Continue drafting the demand letter into the workspace editor. Focus on the following section. 
    Use previous chat history for context. If an image is attached 
    for this section, use it as an example for content and style,
    trying to replicate its format and style exactly. For the intro, include also an intro paragraph. Current section: `;

    try {
      let documentId = null;
      if (workspaceEditor) {
        documentId = workspaceEditor.config.get('documentId') as string;
      }

      // Iterate based on SECTION_ORDER
      // eslint-disable-next-line no-restricted-syntax
      for (const sectionName of SECTION_ORDER) {
        if (shouldStopExecutionRef.current) break;

        const sectionData = analyzedSectionsInternal[sectionName];
        if (!sectionData) {
          console.log(
            `[useOneShotDemand] handleSubmitSections: No data for section "${sectionName}", skipping.`,
          );
          // eslint-disable-next-line no-continue
          continue; // Skip if this section wasn't found in the analysis
        }

        isProcessingSectionRef.current = true; // Lock for the current section processing

        const sectionTitle = sectionName.replace(/_/g, ' ');
        let sectionMessage = `${baseInstruction}"${sectionTitle}".`;
        const sectionAttachments: AgentChatAttachment[] = [];

        const imageUrl = sectionData.image_url;

        if (imageUrl && typeof imageUrl === 'string') {
          try {
            console.log(
              '[useOneShotDemand] handleSubmitSections: Processing image for section:',
              sectionTitle,
              'Image URL:',
              imageUrl,
            );
            const response = await fetchWithRetry(imageUrl);
            const blob = await response.blob();
            const imageFile = new File(
              [blob],
              `${String(sectionName).replace(/\W/g, '_')}.png`,
              { type: blob.type || 'image/png' },
            );
            const uploadedAttachment = await onUploadAttachment(imageFile);
            sectionAttachments.push(uploadedAttachment);
            sectionMessage += ` Please refer to the attached image.`;
          } catch (e: any) {
            console.error(`Failed to process image for ${sectionTitle}:`, e);
            let description = e.message;
            if (
              e instanceof TypeError &&
              e.message.toLowerCase().includes('failed to fetch')
            ) {
              description =
                'Could not retrieve image after multiple attempts. The link may have expired or there might be a network/CORS issue. Please try submitting again or contact support if the problem persists.';
            } else if (e.message && e.message.includes('HTTP error')) {
              description = `Failed to fetch image after multiple attempts: ${e.message}`;
            }
            toast.error(`Image processing for ${sectionTitle} failed.`, {
              description,
            });
          }
        }

        if (shouldStopExecutionRef.current) {
          isProcessingSectionRef.current = false;
          break;
        }

        if (workspaceEditor) {
          const agentChatPlugin = workspaceEditor.plugins.get(
            AgentChatPlugin,
          ) as AgentChatPlugin;
          agentChatPlugin.handleChatStarted();
        }

        await waitForAI(`pre-send for ${sectionTitle}`, 20);
        try {
          await sendMessage(
            sectionMessage,
            sectionAttachments,
            { documentId },
          );
        } finally {
          if (workspaceEditor) {
            const agentChatPlugin = workspaceEditor.plugins.get(
              AgentChatPlugin,
            ) as AgentChatPlugin;
            await agentChatPlugin.handleChatEnded();
          }
        }

        await waitForAI(`post-send for ${sectionTitle}`);
        if (shouldStopExecutionRef.current) {
          isProcessingSectionRef.current = false;
          break;
        }
        isProcessingSectionRef.current = false;
      }

      if (shouldStopExecutionRef.current) {
        toast.info('Section submission stopped.');
      } else {
        toast.success('All sections processed successfully.');
      }
    } catch (error: any) {
      console.error('Error in handleSubmitSections:', error);
      toast.error('An error occurred during section submission.', {
        description: error.message,
      });
    } finally {
      setIsSubmittingSections(false);
      shouldStopExecutionRef.current = false;
      // setAnalyzedSectionsInternal({}); // Keep sections for now, don't clear immediately
      isProcessingSectionRef.current = false;
      // setCurrentDemandExampleId(''); // Already cleared at the start of submit
      // setEffectivePollingStatus(''); // Already cleared
      // setCurrentActualAnalysisStatus(''); // Will be cleared if ID is gone
      setUserHasClosedModal(true);
    }
  };

  const handleStopExecution = () => {
    shouldStopExecutionRef.current = true;
    toast.info(
      'Stop request received. Processing will halt after the current section.',
    );
  };

  return {
    isAnalyzingSections: isSystemTrulyAnalyzing,
    isSubmittingSections,
    analyzedSections: analyzedSectionsInternal,
    showSectionsDialog: _showSectionsDialog,
    currentAnalysisStatus: currentActualAnalysisStatus,
    handleOneShotDemandSelected,
    handleSubmitSections,
    handleStopExecution,
    setShowSectionsDialog,
    setAnalyzedSections: setAnalyzedSectionsInternal,
  };
};
